import { Checkbox, Col, Flex, Form } from 'antd';
import { FormikProps } from 'formik';

import { MInput, MRadio, MSelect, MTextArea } from '@Components/Base';
import MUpload from '@Components/Base/Upload/Upload';
import { ITRequestItem } from '@Features/EmployeeSelfService/requests/ITRequisition/form/ITRequestItem.enum';
import { ITRequestFormValues } from '@Features/EmployeeSelfService/requests/ITRequisition/form/type';
import { Option, OptionNum } from '@Components/types/options';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import { Attachment } from '@Components/types/Attachment';
import { UserTypeCodes } from '@Features/EmployeeSelfService/requests/ITRequisition/form/isNewUser.enum';
import { isMobileNumberValid } from '@Utils/isMobileNumberValid';
import {
  DEFAULT_REPLACEMENT_FEE,
  DEFAULT_CURRENCY,
} from '@Features/EmployeeSelfService/requests/ITRequisition/form/constants';
import {
  requiredRoleOptions,
  accountModuleOptions,
  moduleOptions,
  ModuleOptions,
} from '@Features/EmployeeSelfService/requests/ITRequisition/fieldsOptions';
import { RequiredNotification } from '@Components/index';

import { EMAIL_REQUEST_NOTE } from './constants';

interface ConditionalFormDetailsProps {
  splittedUserName: Array<string>;
  requisitionItem: string;
  simCapValues: OptionNum[];
  itAccessories: Option[];
  formik: FormikProps<ITRequestFormValues>;
  formApproval?: boolean;
  onFileRequest?: (options: RcCustomRequestOptions) => void;
  onFileRemove?: () => void;
  uploadedFile?: Attachment;
  onUploadedFileRemove?: (value: boolean | undefined) => void;
  organizations: Option[];
  projects: Option[];
  onModuleChange?: (value: string[]) => void;
  isModalOpened: boolean;
  onModalCancel: () => void;
  onModalSubmit: () => void;
}

const checkIsModuleAllowedRequiredRole = (module: string[]): boolean => {
  return (
    module.includes(String(ModuleOptions.PURCHASE)) ||
    module.includes(String(ModuleOptions.WGS)) ||
    module.includes(String(ModuleOptions.ACCOUNTS))
  );
};

const ConditionalFormDetails: React.FC<ConditionalFormDetailsProps> = ({
  splittedUserName,
  requisitionItem,
  simCapValues,
  itAccessories,
  formApproval,
  formik,
  onFileRequest,
  onFileRemove,
  uploadedFile,
  onUploadedFileRemove,
  organizations,
  projects,
  onModuleChange,
  isModalOpened,
  onModalCancel,
  onModalSubmit,
}) => {
  const emailsOptions = splittedUserName.map((splittedItem) => ({
    label: splittedItem,
    value: splittedItem,
  }));

  const {
    simCardCreditLimit: simCardCreditLimitTouched,
    contactNo: contactNoTouched,
    emailIdLeft: emailIdLeftTouched,
    emailIdRight: emailIdRightTouched,
    emailSignatureLeft: emailSignatureLeftTouched,
    emailSignatureRight: emailSignatureRightTouched,
    document: documentTouched,
    isNewUser: isNewUserTouched,
    module: moduleTouched,
    organization: organizationTouched,
    projects: projectsTouched,
    accessories: accessoriesTouched,
    intCallsNumber: intCallsNumberTouched,
    countryCode: intCallsCountryCodeTouched,
    remarks: remarksTouched,
  } = formik.touched;
  const {
    simCardCreditLimit: simCardCreditLimitError,
    contactNo: contactNoError,
    emailIdLeft: emailIdLeftError,
    emailIdRight: emailIdRightError,
    emailSignatureLeft: emailSignatureLeftError,
    emailSignatureRight: emailSignatureRightError,
    document: documentError,
    isNewUser: isNewUserError,
    module: moduleError,
    organization: organizationError,
    projects: projectsError,
    accessories: accessoriesError,
    intCallsNumber: intCallsNumberError,
    countryCode: intCallsCountryCodeError,
    remarks: remarksError,
  } = formik.errors;

  const atLeastOneEmailIdInvalid =
    (emailIdLeftError && emailIdLeftTouched) ||
    (emailIdRightError && emailIdRightTouched);

  const atLeastOneEmailSignatureInvalid =
    (emailSignatureLeftError && emailSignatureLeftTouched) ||
    (emailSignatureRightError && emailSignatureRightTouched);

  const { contactNo } = formik.values;

  const contactNoValue = contactNo === null ? {} : { value: contactNo };

  const showRequiredRole = checkIsModuleAllowedRequiredRole(
    formik.values.module
  );
  const showAccountsModule = formik.values.module.includes(
    String(ModuleOptions.ACCOUNTS)
  );

  const onMobileNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const NUMBER_LENGTH_LIMIT = 10;

    const { value } = e.target;
    const isNumberValid = isMobileNumberValid(value, NUMBER_LENGTH_LIMIT);

    if (isNumberValid) {
      formik.setFieldValue('contactNo', value);
    }
  };

  const onMobileNumberBlur = (): void => {
    formik.setFieldTouched('contactNo', true);
  };

  switch (requisitionItem) {
    case ITRequestItem.NEW_SIM_CARD:
      return (
        <>
          <Col span={24}>
            <Form.Item
              label="SIM Card Credit Limit"
              tooltip="AED"
              validateStatus={
                simCardCreditLimitTouched && simCardCreditLimitError
                  ? 'error'
                  : ''
              }
              help={
                simCardCreditLimitError && simCardCreditLimitTouched
                  ? simCardCreditLimitError
                  : ''
              }
            >
              <MSelect
                labeloption="SIM Card Credit Limit"
                value={formik.values.simCardCreditLimit}
                options={simCapValues}
                onChange={(selectValue) => {
                  formik.setFieldValue('simCardCreditLimit', selectValue);
                }}
                onBlur={() => {
                  formik.setFieldTouched('simCardCreditLimit', true);
                }}
                disabled={formApproval}
                placeholder="Choose SIM Card Credit Limit"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Contact No.*"
              tooltip="Enter the mobile number without country code, symbols and space. (Only number allowed with maximum of 10 digit.)"
              validateStatus={contactNoTouched && contactNoError ? 'error' : ''}
              help={contactNoTouched && contactNoError ? contactNoError : ''}
            >
              <MInput
                onChange={onMobileNumberChange}
                onBlur={onMobileNumberBlur}
                disabled={formApproval}
                {...contactNoValue}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Remarks"
              validateStatus={remarksTouched && remarksError ? 'error' : ''}
              help={remarksTouched && remarksError ? remarksError : ''}
            >
              <MTextArea
                name="remarks"
                value={formik.values.remarks}
                onChange={formik.handleChange}
                rows={4}
                showCount
                maxLength={100}
                disabled={formApproval}
                placeholder="Leave a comment"
              />
            </Form.Item>
          </Col>
        </>
      );
    case ITRequestItem.EMAIL:
      return (
        <>
          <Col span={24}>
            <Form.Item
              label="Choose Email ID"
              tooltip="Kindly discuss with employee and select the desired email ID ! Once created we can not able to change the email address!"
              validateStatus={atLeastOneEmailIdInvalid ? 'error' : ''}
              help={
                atLeastOneEmailIdInvalid
                  ? emailIdLeftError || emailIdRightError
                  : ''
              }
            >
              <Flex gap={30}>
                <MSelect
                  labeloption="Choose Email ID"
                  options={emailsOptions}
                  value={formik.values.emailIdLeft}
                  onChange={(selectValue) =>
                    formik.setFieldValue('emailIdLeft', selectValue)
                  }
                  onBlur={() => {
                    formik.setFieldTouched('emailIdLeft', true);
                  }}
                  disabled={formApproval}
                  placeholder="Select"
                />
                <MSelect
                  labeloption="Choose Email ID"
                  options={emailsOptions}
                  value={formik.values.emailIdRight}
                  onChange={(selectValue) =>
                    formik.setFieldValue('emailIdRight', selectValue)
                  }
                  onBlur={() => {
                    formik.setFieldTouched('emailIdRight', true);
                  }}
                  disabled={formApproval}
                  placeholder="Select"
                />
              </Flex>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Choose Email Signature"
              tooltip="Kindly discuss with employee and select the email signature name! Once created we can not able to change the email signature!"
              validateStatus={atLeastOneEmailSignatureInvalid ? 'error' : ''}
              help={
                atLeastOneEmailSignatureInvalid
                  ? emailSignatureLeftError || emailSignatureRightError
                  : ''
              }
            >
              <Flex gap={30}>
                <MSelect
                  labeloption="Choose Email Signature"
                  options={emailsOptions}
                  value={formik.values.emailSignatureLeft}
                  onChange={(selectValue) =>
                    formik.setFieldValue('emailSignatureLeft', selectValue)
                  }
                  onBlur={() => {
                    formik.setFieldTouched('emailSignatureLeft', true);
                  }}
                  disabled={formApproval}
                  placeholder="Select"
                />
                <MSelect
                  labeloption="Choose Email Signature"
                  options={emailsOptions}
                  value={formik.values.emailSignatureRight}
                  onChange={(selectValue) =>
                    formik.setFieldValue('emailSignatureRight', selectValue)
                  }
                  onBlur={() => {
                    formik.setFieldTouched('emailSignatureRight', true);
                  }}
                  disabled={formApproval}
                  placeholder="Select"
                />
              </Flex>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Document"
              tooltip="Upload the approved document from HR."
              validateStatus={documentError && documentTouched ? 'error' : ''}
              help={documentError && documentTouched ? documentError : ''}
            >
              <MUpload
                onFileRequest={onFileRequest}
                onFileRemove={onFileRemove}
                uploadedFile={uploadedFile}
                onUploadedFileRemove={onUploadedFileRemove}
                disabled={formApproval}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Contact No.*"
              tooltip="Enter the mobile number without country code, symbols and space. (Only number allowed with maximum of 10 digit.)"
              validateStatus={contactNoTouched && contactNoError ? 'error' : ''}
              help={contactNoTouched && contactNoError ? contactNoError : ''}
            >
              <MInput
                onChange={onMobileNumberChange}
                onBlur={onMobileNumberBlur}
                disabled={formApproval}
                {...contactNoValue}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Remarks"
              validateStatus={remarksTouched && remarksError ? 'error' : ''}
              help={remarksTouched && remarksError ? remarksError : ''}
            >
              <MTextArea
                name="remarks"
                value={formik.values.remarks}
                onChange={formik.handleChange}
                rows={4}
                showCount
                maxLength={100}
                disabled={formApproval}
                placeholder="Leave a comment"
              />
            </Form.Item>
          </Col>
          <RequiredNotification
            title="Submit Email Request"
            message={EMAIL_REQUEST_NOTE}
            isModalOpen={isModalOpened}
            textSuccessBtn="Continue"
            textCancelBtn="Cancel"
            handleCancel={onModalCancel}
            handleSuccess={onModalSubmit}
          />
        </>
      );
    case ITRequestItem.BUILD_SMART_ACCESS:
      return (
        <>
          <Col span={24}>
            <Form.Item
              label="User Information"
              validateStatus={isNewUserError && isNewUserTouched ? 'error' : ''}
              help={isNewUserError && isNewUserTouched ? isNewUserError : ''}
            >
              <Flex>
                <MRadio
                  id="New User"
                  value={UserTypeCodes.NEW_USER}
                  name="user-information"
                  labelText="New User"
                  onChange={(event) =>
                    formik.setFieldValue('isNewUser', event.target.value)
                  }
                  onBlur={() => {
                    formik.setFieldTouched('isNewUser', true);
                  }}
                  checked={
                    Number(formik.values.isNewUser) === UserTypeCodes.NEW_USER
                  }
                />
                <MRadio
                  id="Existing User"
                  value={UserTypeCodes.EXISTING_USER}
                  name="user-information"
                  labelText="Existing User"
                  onChange={(event) =>
                    formik.setFieldValue('isNewUser', event.target.value)
                  }
                  onBlur={() => {
                    formik.setFieldTouched('isNewUser', true);
                  }}
                  checked={
                    Number(formik.values.isNewUser) ===
                    UserTypeCodes.EXISTING_USER
                  }
                />
              </Flex>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Module"
              validateStatus={moduleError && moduleTouched ? 'error' : ''}
              help={moduleError && moduleTouched ? moduleError : ''}
            >
              <Checkbox.Group
                options={moduleOptions}
                value={formik.values.module}
                disabled={formApproval}
                onChange={(selectValue) => {
                  formik.setFieldValue('module', selectValue).then(() => {
                    if (!moduleTouched) {
                      formik.setFieldTouched('module', true).then(() => {
                        onModuleChange?.(selectValue);
                      });
                    } else {
                      onModuleChange?.(selectValue);
                    }
                  });
                }}
              />
            </Form.Item>
          </Col>
          {showAccountsModule ? (
            <Col span={24}>
              <Form.Item label="Accounts Module">
                <Checkbox.Group
                  options={accountModuleOptions}
                  value={formik.values.accountsModule}
                  disabled={formApproval}
                  onChange={(selectValue) => {
                    formik.setFieldValue('accountsModule', selectValue);
                  }}
                />
              </Form.Item>
            </Col>
          ) : null}
          <Col span={24}>
            <Form.Item
              label="Organization"
              validateStatus={
                organizationError && organizationTouched ? 'error' : ''
              }
              help={
                organizationError && organizationTouched
                  ? organizationError
                  : ''
              }
            >
              <MSelect
                labeloption="Organization"
                options={organizations}
                value={formik.values.organization}
                disabled={formApproval}
                onChange={(selectValue) => {
                  formik.setFieldValue('projects', []);

                  formik.setFieldValue('organization', selectValue);
                }}
                onBlur={() => {
                  formik.setFieldTouched('organization', true);
                }}
                placeholder="Select"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Projects"
              validateStatus={projectsError && projectsTouched ? 'error' : ''}
              help={projectsError && projectsTouched ? projectsError : ''}
            >
              <MSelect
                labeloption="Projects"
                options={projects}
                value={formik.values.projects}
                disabled={formApproval}
                onChange={(selectValue) => {
                  formik.setFieldValue('projects', selectValue);
                }}
                onBlur={() => {
                  formik.setFieldTouched('projects', true);
                }}
                placeholder="Select"
                checkboxOptions
              />
            </Form.Item>
          </Col>
          {showRequiredRole ? (
            <Col span={24}>
              <Form.Item label="Required Role">
                <MSelect
                  labeloption="Required Role"
                  options={requiredRoleOptions}
                  onChange={(selectValue) =>
                    formik.setFieldValue('requiredRole', selectValue)
                  }
                  value={formik.values.requiredRole}
                  onBlur={() => {
                    formik.setFieldTouched('requiredRole', true);
                  }}
                  disabled={formApproval}
                  placeholder="Select"
                />
              </Form.Item>
            </Col>
          ) : null}
          <Col span={24}>
            <Form.Item
              label="Contact No.*"
              tooltip="Enter the mobile number without country code, symbols and space. (Only number allowed with maximum of 10 digit.)"
              validateStatus={contactNoTouched && contactNoError ? 'error' : ''}
              help={contactNoTouched && contactNoError ? contactNoError : ''}
            >
              <MInput
                onChange={onMobileNumberChange}
                onBlur={onMobileNumberBlur}
                disabled={formApproval}
                {...contactNoValue}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Remarks"
              validateStatus={remarksTouched && remarksError ? 'error' : ''}
              help={remarksTouched && remarksError ? remarksError : ''}
            >
              <MTextArea
                name="remarks"
                value={formik.values.remarks}
                onChange={formik.handleChange}
                rows={4}
                showCount
                maxLength={100}
                disabled={formApproval}
                placeholder="Leave a comment"
              />
            </Form.Item>
          </Col>
        </>
      );
    case ITRequestItem.ACCESSORIES:
      return (
        <>
          <Col span={24}>
            <Form.Item
              label="Accessories"
              validateStatus={
                accessoriesError && accessoriesTouched ? 'error' : ''
              }
              help={
                accessoriesError && accessoriesTouched ? accessoriesError : ''
              }
            >
              <MSelect
                labeloption="Accessories"
                options={itAccessories}
                disabled={formApproval}
                value={formik.values.accessories}
                onChange={(selectValue) => {
                  formik.setFieldValue('accessories', selectValue);
                }}
                onBlur={() => {
                  formik.setFieldTouched('accessories', true);
                }}
                placeholder="Select"
                checkboxOptions
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Contact No.*"
              tooltip="Enter the mobile number without country code, symbols and space. (Only number allowed with maximum of 10 digit.)"
              validateStatus={contactNoTouched && contactNoError ? 'error' : ''}
              help={contactNoTouched && contactNoError ? contactNoError : ''}
            >
              <MInput
                onChange={onMobileNumberChange}
                onBlur={onMobileNumberBlur}
                disabled={formApproval}
                {...contactNoValue}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Remarks"
              validateStatus={remarksTouched && remarksError ? 'error' : ''}
              help={remarksTouched && remarksError ? remarksError : ''}
            >
              <MTextArea
                name="remarks"
                value={formik.values.remarks}
                onChange={formik.handleChange}
                rows={4}
                showCount
                maxLength={100}
                disabled={formApproval}
                placeholder="Leave a comment"
              />
            </Form.Item>
          </Col>
        </>
      );
    case ITRequestItem.INTERNATIONAL_CALLS:
      return (
        <>
          <Col span={12}>
            <Form.Item
              label="Country Code"
              validateStatus={
                intCallsCountryCodeError && intCallsCountryCodeTouched
                  ? 'error'
                  : ''
              }
              help={
                intCallsCountryCodeError && intCallsCountryCodeTouched
                  ? intCallsCountryCodeError
                  : ''
              }
            >
              <MInput
                placeholder="Type your country code"
                prefix="+"
                value={formik.values.countryCode}
                onChange={(event) => {
                  if (!Number.isNaN(Number(event.target.value))) {
                    formik.setFieldValue('countryCode', event.target.value);
                  }
                }}
                onBlur={() => {
                  formik.setFieldTouched('countryCode', true);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={11} push={1}>
            <Form.Item
              label="International Calls"
              validateStatus={
                intCallsNumberError && intCallsNumberTouched ? 'error' : ''
              }
              help={
                intCallsNumberError && intCallsNumberTouched
                  ? intCallsNumberError
                  : ''
              }
            >
              <MInput
                disabled={formApproval}
                value={formik.values.intCallsNumber}
                onChange={(e) => {
                  if (Number(e.target.value)) {
                    formik.setFieldValue('intCallsNumber', `${e.target.value}`);
                  }
                }}
                onBlur={() => {
                  formik.setFieldTouched('intCallsNumber', true);
                }}
                placeholder="000 000 0000"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Remarks"
              validateStatus={remarksTouched && remarksError ? 'error' : ''}
              help={remarksTouched && remarksError ? remarksError : ''}
            >
              <MTextArea
                name="remarks"
                value={formik.values.remarks}
                onChange={formik.handleChange}
                rows={4}
                showCount
                maxLength={100}
                disabled={formApproval}
                placeholder="Leave a comment"
              />
            </Form.Item>
          </Col>
        </>
      );
    case ITRequestItem.LOST_SIM_CARD:
      return (
        <>
          <Col span={24}>
            <Form.Item
              label="Mobile"
              validateStatus={contactNoTouched && contactNoError ? 'error' : ''}
              help={contactNoTouched && contactNoError ? contactNoError : ''}
            >
              <MInput
                onChange={onMobileNumberChange}
                onBlur={onMobileNumberBlur}
                disabled
                {...contactNoValue}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Replacement Fee">
              <MInput
                disabled
                value={`${DEFAULT_REPLACEMENT_FEE} ${DEFAULT_CURRENCY}`}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Remarks"
              validateStatus={remarksTouched && remarksError ? 'error' : ''}
              help={remarksTouched && remarksError ? remarksError : ''}
            >
              <MTextArea
                name="remarks"
                value={formik.values.remarks}
                onChange={formik.handleChange}
                rows={4}
                showCount
                maxLength={100}
                disabled={formApproval}
                placeholder="Leave a comment"
              />
            </Form.Item>
          </Col>
        </>
      );
    // TODO: Hide by requirement
    // case ITRequestItem.ASSET_STATUS_CHANGE:
    //   return (
    //     <>
    //       <Col span={24}>
    //         <Form.Item
    //           label="Contact No.*"
    //           tooltip="Enter the mobile number without country code, symbols and space. (Only number allowed with maximum of 10 digit.)"
    //           validateStatus={contactNoTouched && contactNoError ? 'error' : ''}
    //           help={contactNoTouched && contactNoError ? contactNoError : ''}
    //         >
    //           <MInput
    //             onChange={onMobileNumberChange}
    //             onBlur={onMobileNumberBlur}
    //             disabled={formApproval}
    //             {...contactNoValue}
    //           />
    //         </Form.Item>
    //       </Col>
    //       <Col span={24}>
    //         <Form.Item
    //           label="Remarks"
    //           validateStatus={remarksTouched && remarksError ? 'error' : ''}
    //           help={remarksTouched && remarksError ? remarksError : ''}
    //         >
    //           <MTextArea
    //             name="remarks"
    //             value={formik.values.remarks}
    //             onChange={formik.handleChange}
    //             rows={4}
    //             showCount
    //             maxLength={100}
    //             disabled={formApproval}
    //             placeholder="Leave a comment"
    //           />
    //         </Form.Item>
    //       </Col>
    //     </>
    //   );
    case ITRequestItem.SIM_ADDON_REQUEST:
      return (
        <>
          <Col span={24}>
            <Form.Item
              label="Mobile"
              tooltip="Enter the mobile number without country code, symbols and space. (Only number allowed with maximum of 10 digit.)"
              validateStatus={contactNoTouched && contactNoError ? 'error' : ''}
              help={contactNoTouched && contactNoError ? contactNoError : ''}
            >
              <MInput
                onChange={onMobileNumberChange}
                onBlur={onMobileNumberBlur}
                disabled
                {...contactNoValue}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Remarks"
              validateStatus={remarksTouched && remarksError ? 'error' : ''}
              help={remarksTouched && remarksError ? remarksError : ''}
            >
              <MTextArea
                name="remarks"
                value={formik.values.remarks}
                onChange={formik.handleChange}
                rows={4}
                showCount
                maxLength={100}
                disabled={formApproval}
                placeholder="Leave a comment"
              />
            </Form.Item>
          </Col>
        </>
      );
    default:
      return null;
  }
};

export default ConditionalFormDetails;
